/**
 * Screen 33 — Chat privacy + blocked users. Visual structure mirrors
 * Triviana_Wireframes.html screen 33: `<h3 class="sec">` headers,
 * `.card` groups, `.opt` rows for radio + toggle controls.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { fetchChatPrefs, patchChatPrefs } from '../lib/messaging.js';


const WHO_OPTIONS = [
  ['everyone', 'Everyone', 'Anyone can send a request — first message goes to Requests'],
  ['friends', 'Friends only', 'Only accepted friends can message you'],
  ['nobody', 'No one', 'Messaging is off. You can still be challenged to duels'],
];


function shell() {
  return `
    <div class="appbar">
      <a href="/messages" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Chat privacy</div>
      <div class="appbar-spacer"></div>
    </div>
    <div class="pad">
      <h3 class="sec"><span class="dot"></span>Who can message me</h3>
      <div class="card" style="padding:6px;" id="who-group">
        ${WHO_OPTIONS.map(
          ([val, title, desc]) => `
          <div class="opt" data-who="${escape(val)}" style="border:0;background:transparent;padding:11px 8px;cursor:pointer;">
            <div class="grow">
              <b style="font-size:13.5px;color:#fff;font-weight:600;">${escape(title)}</b>
              <div class="muted" style="font-size:11px;font-weight:500;margin-top:1px;">${escape(desc)}</div>
            </div>
            <span class="who-dot" style="width:20px;height:20px;border-radius:11px;border:2px solid #3A2F5F;display:flex;align-items:center;justify-content:center;flex:none;">
              <span class="who-dot-inner" style="width:10px;height:10px;border-radius:6px;background:var(--teal);display:none;"></span>
            </span>
          </div>
        `,
        ).join('')}
      </div>

      <h3 class="sec">Preferences</h3>
      <div class="opt" data-toggle="read_receipts" style="cursor:pointer;">
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">Read receipts</b>
          <div class="muted" style="font-size:11px;font-weight:500;margin-top:1px;">Let others see when their message has been read.</div>
        </div>
        <span class="toggle-track" style="width:42px;height:24px;border-radius:12px;background:#2E2748;position:relative;flex:none;">
          <span class="toggle-knob" style="position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:9px;background:#fff;transition:left 0.15s;"></span>
        </span>
      </div>
      <div class="opt" data-toggle="push" style="cursor:pointer;">
        <div class="grow">
          <b style="font-size:13px;color:#fff;font-weight:600;">Message notifications</b>
          <div class="muted" style="font-size:11px;font-weight:500;margin-top:1px;">Push + in-app alerts for new messages.</div>
        </div>
        <span class="toggle-track" style="width:42px;height:24px;border-radius:12px;background:#2E2748;position:relative;flex:none;">
          <span class="toggle-knob" style="position:absolute;top:3px;left:3px;width:18px;height:18px;border-radius:9px;background:#fff;transition:left 0.15s;"></span>
        </span>
      </div>

      <h3 class="sec">Blocked users</h3>
      <div id="blocked-list">
        <div class="muted center" style="padding:16px;font-size:11.5px;">Loading…</div>
      </div>
    </div>
  `;
}


function applyRadio(root, who) {
  root.querySelectorAll('.opt[data-who]').forEach((opt) => {
    const active = opt.dataset.who === who;
    const dot = opt.querySelector('.who-dot');
    const inner = opt.querySelector('.who-dot-inner');
    if (dot) dot.style.borderColor = active ? 'var(--teal)' : '#3A2F5F';
    if (inner) inner.style.display = active ? '' : 'none';
  });
}


function applyToggle(root, name, on) {
  const opt = root.querySelector(`.opt[data-toggle="${name}"]`);
  if (!opt) return;
  const track = opt.querySelector('.toggle-track');
  const knob = opt.querySelector('.toggle-knob');
  if (track) track.style.background = on ? 'var(--teal)' : '#2E2748';
  if (knob) knob.style.left = on ? '21px' : '3px';
  opt.dataset.on = on ? 'true' : 'false';
}


async function paintPrefs(root) {
  try {
    const prefs = await fetchChatPrefs();
    applyRadio(root, prefs.who_can_message || 'everyone');
    applyToggle(root, 'read_receipts', prefs.read_receipts !== false);
    applyToggle(root, 'push', prefs.push !== false);
  } catch (err) {
    toast("Couldn't load your privacy settings");
    console.error('chat prefs load failed', err);
  }
}


function wirePrefs(root) {
  root.querySelectorAll('.opt[data-who]').forEach((opt) => {
    opt.addEventListener('click', async () => {
      const who = opt.dataset.who;
      applyRadio(root, who);
      try {
        await patchChatPrefs({ who_can_message: who });
        toast('Saved');
      } catch (err) {
        toast(err.message || 'Save failed');
      }
    });
  });
  root.querySelectorAll('.opt[data-toggle]').forEach((opt) => {
    opt.addEventListener('click', async () => {
      const name = opt.dataset.toggle;
      const next = opt.dataset.on !== 'true';
      applyToggle(root, name, next);
      try {
        await patchChatPrefs({ [name]: next });
        toast('Saved');
      } catch (err) {
        // Revert on failure.
        applyToggle(root, name, !next);
        toast(err.message || 'Save failed');
      }
    });
  });
}


async function paintBlocked(root) {
  const container = root.querySelector('#blocked-list');
  try {
    const res = await api('/friends/blocks');
    const items = (res && res.items) || [];
    if (!items.length) {
      container.innerHTML = '<div class="muted small" style="padding:8px 2px;font-size:11.5px;">No blocked users.</div>';
      return;
    }
    container.innerHTML = items
      .map((row) => {
        const u = row.user || {};
        return `
          <div class="card" style="padding:10px;" data-user-id="${escape(u.id || '')}">
            <div class="row">
              <div class="av" style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#fff;background:#2A2140;">${escape((u.username || '?')[0].toUpperCase())}</div>
              <div class="grow">
                <b style="font-size:13px;color:#fff;font-weight:600;">${escape(u.username || 'Player')}</b>
                <div class="muted" style="font-size:11px;font-weight:500;">Can't message, challenge or view you</div>
              </div>
              <button type="button" class="btn ghost sm" data-unblock style="margin:0;width:auto;padding:7px 12px;">Unblock</button>
            </div>
          </div>
        `;
      })
      .join('');
    container.querySelectorAll('[data-unblock]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const row = btn.closest('.card');
        const uid = row && row.dataset.userId;
        if (!uid) return;
        btn.disabled = true;
        try {
          await api(`/friends/blocks/${encodeURIComponent(uid)}`, { method: 'DELETE' });
          row.remove();
          toast('Unblocked');
        } catch (err) {
          btn.disabled = false;
          toast(err.message || 'Unblock failed');
        }
      });
    });
  } catch (err) {
    container.innerHTML = '<div class="muted small" style="padding:8px 2px;font-size:11.5px;">Couldn\'t load blocked users.</div>';
    console.error('blocked list load failed', err);
  }
}


export async function renderChatPrivacy(root) {
  if (!auth.isAuthenticated()) {
    navigate('/onboarding');
    return;
  }
  root.innerHTML = shell();
  await paintPrefs(root);
  wirePrefs(root);
  paintBlocked(root);
}
